// Route contracts. Each HTTP operation of the slice is one `defineRoute` value: method, path,
// access rule, feature gate, request and response schemas, documented problem codes and the
// events it emits. The OpenAPI document (`src/openapi.ts`), the SDK (`packages/sdk`) and the
// route-coverage test of services/api are all generated from or checked against these values.
import type { z } from "zod";
import type { FeatureKey } from "../enums";
import type { EventType } from "../events";
import type { ProblemCode } from "../problem";

export type HttpMethod = "get" | "post" | "put" | "patch" | "delete";

/**
 * Who may call a route (13 §Principals and roles). The API resolves it to a `can()` relation;
 * the isolation harness (S1-05) reads it to pick principals.
 * - `public`: no credential (`/health`, `/ready`).
 * - `session`: any signed-in user (`/v1/me`).
 * - `operator`: session + `agency_user` (+ an operator grant for company-scoped operator routes).
 * - `manager`: `company#manager` (owner | admin | agency_operator).
 * - `reader`: `company#reader` (manager | client_viewer), narrowed per row by visibility. Company
 *   data a client reads (scorecards, metrics, costs, time): a plain member is not a reader.
 * - `viewer`: `company#viewer` (reader | member; 05 `company#viewer`), narrowed per row like
 *   `reader`. The app shell, the org chart, teams, seats and the agent roster (MEMBER-ROLE).
 * - `seat_viewer`: `seat#can_view` or, for publications/reports, `company#client_viewer`.
 * - `ingest_key`: `Authorization: Bearer cosk_in_…` bound to the DE (fix #29).
 * - `member`: `company#member` (05), narrowed per object by its own relation (`space#viewer`,
 *   `channel#poster`, `task#editor`, …). Spaces, chat and work routes (M2-01).
 * - `agent_key`: `Authorization: Bearer cosk_de_…` (an `agent` or `service` key, M3-01), bound
 *   to its principal and seat and checked against the route's scope (04 §Auth model). Routes
 *   the agent runtime calls (`runs`, `wakeups/pending`, `agent-keys/rotate`).
 * - `recall_signature`: no principal; a Recall webhook delivery signed with the company tenant's
 *   webhook secret (`X-Recall-Signature`, 07 §Webhooks; M7-03). The signature is the credential.
 * - `hook_signature`: no principal; a delivery to one incoming webhook, signed with that hook's
 *   secret (CompanyOS `t=,v1=`, Stripe or GitHub scheme; WEBHOOKS-IN). The signature is the
 *   credential; an unknown hook is 404 before anything is read.
 * - `voice_service`: no principal; the voice service's own credential
 *   (`Authorization: Bearer <VOICE_SERVICE_TOKEN>`, VOICE-CHOICES), scoped to opening the voice
 *   sessions it hosts and reporting their usage. A person's session or key is refused (401).
 * - `stripe_signature`: no principal; a Stripe event (`Stripe-Signature: t=,v1=`) signed with the
 *   platform's `STRIPE_WEBHOOK_SECRET` (WALLET-P1). The signature is the credential; the company
 *   is the one the API named in the Checkout Session it created, re-checked against its row.
 * - `run_token`: the short-lived run capability token of a running run (04 §Auth model, review
 *   #29; ADR-0019 §1), bound to principal, owner, context refs, allowed actions, budget and
 *   deadline; the DE's own agent key bound to the run's seat is accepted too.
 * - `assessment_token`: no principal; the magic-link token of one assessment respondent in the
 *   path (M8-ASSESS), stored hashed, expiring, bound to its company and its questionnaire only.
 *   BIZ-CLIENT-HEALTH: a client survey's respondent link is the same kind of credential (`survey.*`).
 * - `operator_or_agent_key`: an `operator` session, or the agent key of a seat on the agency
 *   roster holding the route's scope (DC-01; ADR-0018 §6: the Deployment engineer's key with
 *   `deploys:stage` stages and reads deploys and nothing else in the agency console).
 */
export type RouteAccess =
  | "public"
  | "session"
  | "operator"
  | "manager"
  | "reader"
  | "viewer"
  | "seat_viewer"
  | "ingest_key"
  | "member"
  | "agent_key"
  | "run_token"
  | "operator_or_agent_key"
  | "recall_signature"
  | "hook_signature"
  | "voice_service"
  | "stripe_signature"
  // INC2-INTAKE: no principal; a vendor app's notice (GoHighLevel Ed25519, HubSpot v3 HMAC)
  // signed with the platform's app key, naming the vendor account in its body; the company is the
  // one that account's subscription belongs to (`cp.integration_route`), read after the signature.
  | "vendor_signature"
  | "assessment_token"
  // CALENDAR-C1: no principal; a person's ICS feed token in the path (stored hashed, revocable,
  // bound to its company and its person), read-only.
  | "feed_token"
  // AGENT-SPEC: no principal; a Hermes container's scoped identity (`Bearer cosk_ctr_…`, issued
  // by the panel at provisioning, stored as its SHA-256, bound to one satellite + container).
  // Any other token, or one container's token on another's path, is 403.
  | "container_token";

/**
 * `text/event-stream`: Server-Sent Events (ADR-0017 `GET changes`, RT-01; run step streams, M3-01).
 * `application/octet-stream`: a file download whose concrete type the request chooses (M5-03
 * finance exports: `text/csv` or the XLSX type, set on the response with its file name).
 */
export type ResponseContentType =
  | "application/json"
  | "text/html"
  | "application/pdf"
  | "text/event-stream"
  | "application/octet-stream";

export type RouteDef = {
  /** operationId and SDK method key: `{group}.{action}`. */
  id: string;
  method: HttpMethod;
  /** OpenAPI path template (`/v1/companies/{co}/seats/{id}`). */
  path: string;
  summary: string;
  description?: string;
  tag: string;
  feature: z.infer<typeof FeatureKey>;
  access: RouteAccess;
  params?: z.ZodObject;
  query?: z.ZodObject;
  body?: z.ZodType;
  /** `multipart` for CSV imports (`file` + `dry_run`). */
  bodyType?: "json" | "multipart";
  /** `Idempotency-Key` header (04 §Idempotency keys). */
  idempotency?: "required" | "optional";
  /** `If-Match: "{version}"` (04 §Optimistic concurrency). */
  ifMatch?: boolean;
  response: {
    status: 200 | 201 | 202 | 204;
    schema?: z.ZodType;
    contentType?: ResponseContentType;
  };
  /** Problem codes beyond the defaults every route can return (see `defaultProblems`). */
  errors: readonly ProblemCode[];
  /** Event types a successful call may write (03 §Outbox rules 1). Empty for reads. */
  emits: readonly EventType[];
  /**
   * A POST that changes no state (M1-02: `org-chart/simulate`; later `tasks/search`, presigned
   * upload URLs). It is a read for the event rules: it emits nothing and has no obligation.
   */
  safe?: true;
};

export function defineRoute<const R extends RouteDef>(route: R): R {
  return route;
}

/** Reads: GETs and `safe` POSTs. They emit nothing and carry no event obligation. */
export function isReadRoute(route: Pick<RouteDef, "method" | "safe">): boolean {
  return route.method === "get" || route.safe === true;
}

/** Accesses no session reaches (so no two-factor requirement applies). */
const KEY_ONLY = new Set<RouteAccess>([
  "public",
  "ingest_key",
  "agent_key",
  "run_token",
  "recall_signature",
  "hook_signature",
  "voice_service",
  "stripe_signature",
  "vendor_signature",
  "container_token",
]);

/** Problems any route of a given kind may return, added to `errors` by the generators. */
export function defaultProblems(route: RouteDef): ProblemCode[] {
  const codes: ProblemCode[] = ["rate_limited", "internal"];
  // M8-ASSESS: a respondent's routes take the questionnaire token alone (no session).
  const tokenOnly = route.access === "assessment_token" || route.access === "feed_token";
  // AGENT-SPEC: a refused container token is 403 alike, whatever was wrong with it (no oracle).
  if (route.access === "container_token") codes.push("forbidden");
  else {
    if (route.access !== "public" && !tokenOnly) codes.push("unauthenticated");
    if (tokenOnly) codes.push("not_found");
    else if (route.access !== "public" && route.access !== "session")
      codes.push("forbidden", "not_found");
  }
  // HARDEN-mfa: a session that must enrol in two-factor sign-in reaches only `/v1/me` (and auth).
  if (!KEY_ONLY.has(route.access) && !tokenOnly && route.id !== "me.get")
    codes.push("mfa_enrollment_required");
  if (route.body || route.query || route.params) codes.push("validation_failed");
  if (route.feature !== "core") {
    codes.push("feature_disabled");
    if (!isReadRoute(route)) codes.push("feature_read_only");
  }
  if (route.idempotency) codes.push("idempotency_mismatch");
  if (route.idempotency === "required") codes.push("idempotency_key_required");
  if (route.ifMatch) codes.push("conflict", "precondition_required");
  return codes;
}

/** `/v1/companies/{co}/seats/{id}` → `/v1/companies/:co/seats/:id` (Hono). */
export function honoPath(path: string): string {
  return path.replace(/\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g, ":$1");
}

/** Path template placeholders, in order. */
export function pathParams(path: string): string[] {
  return [...path.matchAll(/\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g)].map((m) => m[1] as string);
}
